// src/pages/lead/LeadAttendance.jsx
// LeadAttendance now includes Team Lead's own attendance controls
// with check-in/check-out and breaks, plus team attendance view.
// Enhanced: Supports filtering via URL params for dashboard navigation.

import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  formatTime,
  getTodayStr,
} from "../../hooks/useFirestore";
import { useLeadData } from "../../context/LeadContext";
import { getNavItems, getPanelLabel } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import { isPresent, isWorking, isCheckedOut } from "../../utils/attendanceStatus";
import { useHolidays, resolveEmployeeStatus } from "../../hooks/useFirestoreExtensions";

// ── Shared attachment helpers (same as AdminAnnouncements) ──
// ──────────────────────────────────────────────────────────────
// LeadAttendance (ENHANCED: Team Lead's own attendance + team view)
// ──────────────────────────────────────────────────────────────
export function LeadAttendance() {
  const { userProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const uid = userProfile?.uid;
  const navItems = getNavItems(userProfile);
  const panelLabel = getPanelLabel(userProfile);

  // Get filter from URL params
  const filter = searchParams.get("filter");

  // Team & Other data from LeadContext
  const { 
    members, 
    allAttendance: allAtt, 
    attLoading: loading,
    allLeaves,
  } = useLeadData();


  // Team attendance processing (existing logic)
  const memberIds = new Set(members.map((m) => m.uid));
  const teamAtt = allAtt.filter(
    (a) => memberIds.has(a.userId) && a.userId !== uid,
  );

  const today = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const { data: holidays } = useHolidays();
  const todayStr = getTodayStr();
  const todayHoliday = holidays.find((h) => h.date === todayStr);

  const rows = useMemo(() => {
    return members.map((m) => {
      const att = teamAtt.find((a) => a.userId === m.uid) || null;
      const empLeaves = allLeaves.filter(
        (l) => l.userId === m.uid && l.fromDate <= todayStr && l.toDate >= todayStr
      );
      const resolved = resolveEmployeeStatus(m, att, empLeaves, todayHoliday, todayStr);
      if (att) {
        return { ...att, _resolved: resolved, _hasRecord: true };
      } else {
        return {
          userId: m.uid,
          displayName: m.displayName,
          department: m.department,
          employeeId: m.employeeId,
          status: resolved.status,
          checkIn: null,
          checkOut: null,
          hoursWorked: 0,
          mode: "—",
          isLate: false,
          _resolved: resolved,
          _hasRecord: false,
        };
      }
    });
  }, [members, teamAtt, allLeaves, todayHoliday, todayStr]);

  // Apply filtering based on URL param
  const filteredRows = rows.filter((row) => {
    if (!filter) return true;

    const resolvedStatus = row._resolved?.status || row.status;
    switch (filter) {
      case "present":
        return (
          isPresent(row) &&
          !(row.breaks || []).find((b) => !b.endTime)
        );
      case "absent":
        return resolvedStatus === "absent" || resolvedStatus === "on_leave";
      case "onleave":
        return resolvedStatus === "on_leave";
      default:
        return true;
    }
  });

  const [selectedStatusCard, setSelectedStatusCard] = useState(null);

  function handleChipClick(key) {
    setSelectedStatusCard((prev) => (prev === key ? null : key));
  }

  // ── per-chip predicates ──────────────────────────────────────
  function isOnBreak(r)  { return !!(r.breaks || []).find((b) => !b.endTime); }
  function isWorking_(r) { return isPresent(r) && !isOnBreak(r); }
  function isAbsent_(r)  { return !isPresent(r); }
  function isRemote_(r)  { return r.mode === "remote"; }

  const chipCfg = {
    working:  { label: "Working",  color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", chipClass: "green",  count: rows.filter(isWorking_).length,  match: isWorking_ },
    on_break: { label: "On Break", color: "#d97706", bg: "#fffbeb", border: "#fde68a", chipClass: "amber",  count: rows.filter(isOnBreak).length,   match: isOnBreak  },
    absent:   { label: "Absent",   color: "#dc2626", bg: "#fef2f2", border: "#fecaca", chipClass: "red",    count: rows.filter(isAbsent_).length,   match: isAbsent_  },
    remote:   { label: "Remote",   color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", chipClass: "blue",   count: rows.filter(isRemote_).length,   match: isRemote_  },
  };

  return (
    <Layout navItems={navItems} panelLabel={panelLabel}>
      <div className="page-header">
        <h1 className="page-title">Team Attendance</h1>
        <p className="page-subtitle">{today}</p>
      </div>

      {/* ── Clickable Summary Chips ── */}
      <div className="attendance-summary-row">
        {Object.entries(chipCfg).map(([key, cfg]) => (
          <div
            key={key}
            className={`summary-chip summary-chip--${cfg.chipClass}`}
            onClick={() => handleChipClick(key)}
            title={`Click to see ${cfg.label} members`}
            style={{
              cursor: "pointer",
              outline: selectedStatusCard === key ? "2px solid currentColor" : "2px solid transparent",
              outlineOffset: "2px",
              transform: selectedStatusCard === key ? "scale(1.04)" : "scale(1)",
              transition: "transform 0.15s, outline 0.15s",
              boxShadow: selectedStatusCard === key ? "0 4px 14px rgba(0,0,0,0.12)" : undefined,
              userSelect: "none",
            }}
          >
            <span className="summary-chip-count">{cfg.count}</span>
            <span className="summary-chip-label">{cfg.label}</span>
          </div>
        ))}
      </div>

      {/* ── Drill-Down Panel ── */}
      {selectedStatusCard && (() => {
        const cfg = chipCfg[selectedStatusCard];
        const drillRows = rows.filter(cfg.match);
        return (
          <div style={{
            marginTop: "0.75rem",
            border: `1.5px solid ${cfg.border}`,
            borderRadius: "12px",
            background: cfg.bg,
            overflow: "hidden",
            animation: "fadeInDown 0.2s ease",
          }}>
            {/* Panel header */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "0.75rem 1.1rem",
              borderBottom: `1px solid ${cfg.border}`,
            }}>
              <span style={{ fontWeight: 700, fontSize: "0.95rem", color: cfg.color }}>
                {cfg.label} — {drillRows.length} Member{drillRows.length !== 1 ? "s" : ""}
              </span>
              <button
                onClick={() => setSelectedStatusCard(null)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: cfg.color, fontWeight: 700, fontSize: "1.1rem",
                  lineHeight: 1, padding: "0 4px",
                }}
                title="Close"
              >✕</button>
            </div>

            {/* Panel body */}
            {drillRows.length === 0 ? (
              <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.9rem" }}>
                No employees found for this status.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                  <thead>
                    <tr style={{ background: cfg.border }}>
                      {["Employee", "Employee ID", "Department", "Designation", "Punch-In", "Punch-Out", "Status"].map((h) => (
                        <th key={h} style={{ padding: "0.55rem 0.9rem", textAlign: "left", fontWeight: 700, color: cfg.color, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {drillRows.map((r, i) => {
                      const memberProfile = members.find((m) => m.uid === r.userId);
                      const activeBreak = (r.breaks || []).find((b) => !b.endTime);
                      const statusLabel = activeBreak
                        ? (activeBreak.breakType === "lunch" ? "On Lunch" : activeBreak.breakType === "tea1" ? "On Tea 1" : "On Tea 2")
                        : isWorking(r) ? "Working"
                        : isCheckedOut(r) ? "Checked Out"
                        : r.status?.toUpperCase() || "—";
                      const statusColor = activeBreak ? "amber" : isWorking(r) ? "green" : !isPresent(r) ? "red" : "blue";
                      return (
                        <tr key={r.userId + i} style={{ borderBottom: `1px solid ${cfg.border}`, background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.5)" }}>
                          <td style={{ padding: "0.5rem 0.9rem", fontWeight: 600 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                              <div style={{
                                width: 28, height: 28, borderRadius: "50%",
                                background: cfg.color, color: "#fff",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "0.7rem", fontWeight: 700, flexShrink: 0,
                              }}>
                                {r.displayName?.slice(0, 2).toUpperCase()}
                              </div>
                              {r.displayName}
                            </div>
                          </td>
                          <td style={{ padding: "0.5rem 0.9rem", fontFamily: "var(--font-mono)", color: "var(--text-secondary)" }}>{r.employeeId || "—"}</td>
                          <td style={{ padding: "0.5rem 0.9rem" }}>{r.department || "—"}</td>
                          <td style={{ padding: "0.5rem 0.9rem", color: "var(--text-secondary)" }}>{memberProfile?.designation || "—"}</td>
                          <td style={{ padding: "0.5rem 0.9rem", fontFamily: "var(--font-mono)" }}>{formatTime(r.checkIn) || "—"}</td>
                          <td style={{ padding: "0.5rem 0.9rem", fontFamily: "var(--font-mono)" }}>{formatTime(r.checkOut) || "—"}</td>
                          <td style={{ padding: "0.5rem 0.9rem" }}>
                            <span className={`status-pill status-pill--${statusColor}`}>{statusLabel}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Main table (existing — untouched) ── */}
      {loading ? (
        <div className="card-loading">Loading…</div>
      ) : (
        <div className="data-table-wrap" style={{ marginTop: "20px" }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Status</th>
                <th>Punch-In</th>
                <th>Punch-Out</th>
                <th>Hours</th>
                <th>Mode</th>
                <th>Breaks</th>
                <th>Late?</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r, i) => {
                const activeBreak = (r.breaks || []).find((b) => !b.endTime);
                const resolvedStatus = r._resolved?.status || r.status;
                const statusLabel = activeBreak
                  ? activeBreak.breakType === "lunch"
                    ? "On Lunch"
                    : activeBreak.breakType === "tea1"
                      ? "On Tea 1"
                      : "On Tea 2"
                  : isWorking(r)
                    ? "Working"
                    : isCheckedOut(r)
                      ? (r.status === "AUTO-CHECKED-OUT" || r.status === "auto-checked-out" ? "Auto Checked-Out" : "Completed")
                      : (r._resolved?.label || r.status);
                const statusColor = activeBreak
                  ? "amber"
                  : isWorking(r)
                    ? "green"
                    : !isPresent(r)
                      ? (resolvedStatus === "on_leave" || resolvedStatus === "pending_leave" ? "amber" : resolvedStatus === "rejected_leave" ? "gray" : "red")
                      : isCheckedOut(r)
                        ? "blue"
                        : "gray";
                return (
                  <tr key={r.userId + i}>
                    <td>
                      <div className="table-user-cell">
                        <div className="table-avatar">
                          {r.displayName?.slice(0, 2).toUpperCase()}
                        </div>
                        {r.displayName}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`status-pill status-pill--${statusColor}`}
                      >
                        {statusLabel}
                      </span>
                    </td>
                    <td>{formatTime(r.checkIn)}</td>
                    <td>{formatTime(r.checkOut)}</td>
                    <td>{r.hoursWorked > 0 ? `${r.hoursWorked}h` : "—"}</td>
                    <td style={{ textTransform: "capitalize" }}>{r.mode}</td>
                    <td>
                      {(r.breaks || []).length === 0 ? (
                        "—"
                      ) : (
                        <div style={{ fontSize: "0.75rem", lineHeight: 1.6 }}>
                          {(r.breaks || []).map((b, bi) => (
                            <div key={bi} style={{ whiteSpace: "nowrap" }}>
                              <span style={{ fontWeight: 600 }}>
                                {b.breakType === "tea1"
                                  ? "Tea1"
                                  : b.breakType === "tea2"
                                    ? "Tea2"
                                    : "Lunch"}
                              </span>{" "}
                              {formatTime(b.startTime)}
                              {b.endTime ? (
                                <span style={{ color: "#6c757d" }}>
                                  {" → "}
                                  {formatTime(b.endTime)}{" "}
                                  <span
                                    style={{
                                      color:
                                        b.duration >
                                        (b.breakType === "lunch" ? 60 : 15)
                                          ? "#e74c3c"
                                          : "#2ecc71",
                                    }}
                                  >
                                    ({b.duration}m)
                                    {b.duration > (b.breakType === "lunch" ? 60 : 15) && ` - Excess: ${b.duration - (b.breakType === "lunch" ? 60 : 15)}m`}
                                  </span>
                                </span>
                              ) : (
                                <span
                                  style={{ color: "#f39c12", fontWeight: 700 }}
                                >
                                  {" "}
                                  (Active)
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      {r.isLate ? (
                        <span className="status-pill status-pill--red">
                          +{r.lateByMinutes}m
                        </span>
                      ) : r.checkIn ? (
                        <span className="status-pill status-pill--green">
                          On time
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}